import { MetaProvider } from "@solidjs/meta";
import { type ParentComponent, Show } from "solid-js";
import { PostHogIdentifier } from "@/domains/auth/components/PostHogIdentifier";
import { AuthProviderV2 } from "@/domains/auth/context/auth-provider";
import { BillingProvider } from "@/domains/billing/context/billing-provider";
import { isBillingEnabled } from "@/domains/billing/utils/is-billing-enabled";
import { useHydrateCurrentMemberRole } from "@/domains/company/company/hooks/useHydrateCurrentMemberRole";
import { CompanyProvider } from "@/domains/company/company/store/company-store";
import { ReportGenerationProvider } from "@/domains/company/report/context/report-generation-provider";
import { useBootstrapProfile } from "@/domains/freelance/profile/api";
import { VisitedJobProvider } from "@/domains/job-offer/store/visited_job";
import { I18nProvider } from "./api/i18n";
import { SplashScreen } from "./components/layout/SplashScreen";
import { Toaster } from "./components/ui/sonner";

const CompanyMemberRoleHydrator: ParentComponent = (props) => {
  useHydrateCurrentMemberRole();
  return <>{props.children}</>;
};

const AppProviderManager: ParentComponent = (props) => {
  const session = useBootstrapProfile();
  const visitedJobs = () =>
    session.data?.user_applications?.map((job) => ({
      id: job.job_offer_id,
    })) ?? [];

  return (
    <I18nProvider>
      <MetaProvider>
        <Show when={session.isSuccess} fallback={<SplashScreen />}>
          <AuthProviderV2
            user={null}
            profile={
              session.data?.profile
                ? {
                    id: session.data!.profile!.id,
                    email: session.data!.profile!.email,
                    full_name: session.data!.profile!.full_name,
                    username: session.data!.profile!.username,
                    onboarding_status: session.data!.profile!.onboarding_status,
                    personal_profile_status:
                      session.data!.profile!.personal_profile_status ??
                      session.data!.profile!.onboarding_status,
                    payment_profile: session.data?.profile?.payment_profile,
                    profile_type: session.data?.profile?.profile_type,
                    assets: session.data?.profile?.assets,
                    weekly_hours: session.data?.profile?.weekly_hours,
                    available_from: session.data?.profile?.available_from,
                    avatar_url: session.data?.profile?.avatar_url,
                    last_selected_workspace_id:
                      session.data?.profile?.last_selected_workspace_id,
                    billing_status: session.data?.profile?.billing_status,
                  }
                : null
            }
            companies={session.data?.companies ?? []}
            user_applications={session.data?.user_applications ?? []}
            capabilities={session.data?.capabilities ?? null}
            next_step={session.data?.next_step ?? null}
            active_subscription_id={
              session.data?.active_subscription_id ?? null
            }
            loading={false}
          >
            <PostHogIdentifier />
            <CompanyProvider
              initialState={{
                selectedCompany: session.data?.selectedCompany ?? null,
                currentMemberRole: null,
              }}
            >
              <Show
                when={isBillingEnabled()}
                fallback={
                  <CompanyMemberRoleHydrator>
                    <VisitedJobProvider
                      initialState={{
                        visitedJobs: visitedJobs(),
                      }}
                    >
                      <ReportGenerationProvider>
                        {props.children}
                      </ReportGenerationProvider>
                    </VisitedJobProvider>
                  </CompanyMemberRoleHydrator>
                }
              >
                <BillingProvider>
                  <CompanyMemberRoleHydrator>
                    <VisitedJobProvider
                      initialState={{
                        visitedJobs: visitedJobs(),
                      }}
                    >
                      <ReportGenerationProvider>
                        {props.children}
                      </ReportGenerationProvider>
                    </VisitedJobProvider>
                  </CompanyMemberRoleHydrator>
                </BillingProvider>
              </Show>
            </CompanyProvider>
          </AuthProviderV2>
        </Show>
        <Toaster position="bottom-right" />
      </MetaProvider>
    </I18nProvider>
  );
};

export default AppProviderManager;
